import { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "../lib/api";
import { Sparkles, Send, Bot, User, X, MessageSquare, Cpu } from "lucide-react";

const MODELS = [
  { key: "claude", label: "Claude Sonnet 4.6", short: "Claude" },
  { key: "gpt", label: "GPT-5.4", short: "GPT" },
  { key: "gemini", label: "Gemini 3.1 Pro", short: "Gemini" },
];
const PROMPTS = [
  "Analyse mon chiffre d'affaires et mes marges",
  "Quel taux horaire devrais-je facturer ?",
  "Quels produits sont les plus rentables ?",
];

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("claude");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      setLoaded(true);
      api.get("/ai/history?session_id=default").then((r) => setMessages(r.data)).catch(() => {});
    }
  }, [open, loaded]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming, open]);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-assistant", h);
    return () => window.removeEventListener("open-assistant", h);
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        credentials: "include",
        body: JSON.stringify({ message: msg, model, session_id: "default" }),
      });
      if (!res.ok || !res.body) throw new Error("chat request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const { content } = JSON.parse(data);
            if (content) setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + content };
              return copy;
            });
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: "Erreur de connexion à l'assistant." }; return copy; });
    } finally { setStreaming(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-fab"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-navy text-cognac-400 shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {!open && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-cognac-400 border-2 border-white" />}
      </button>

      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden animate-fade-up">
          <div className="px-4 py-3 bg-navy text-white flex items-center gap-2.5 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-cognac-500/90 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-sm font-display font-bold">Assistant IA Fabriko</div>
              <div className="text-[11px] text-slate-300">Analyse & conseils d'atelier</div>
            </div>
          </div>

          {/* Sélecteur de modèle IA */}
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0 bg-slate-50/70" data-testid="assistant-model-bar">
            <span className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1 shrink-0"><Cpu className="w-3.5 h-3.5 text-cognac-500" /> Modèle IA</span>
            <div className="flex gap-1 flex-1 justify-end">
              {MODELS.map((m) => (
                <button key={m.key} onClick={() => setModel(m.key)} title={m.label} data-testid={`assistant-model-${m.key}`}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors ${model === m.key ? "bg-cognac-500 text-white shadow-sm shadow-cognac-500/25" : "bg-white text-slate-500 border border-slate-200 hover:border-cognac-400 hover:text-cognac-600"}`}>{m.short}</button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Bot className="w-9 h-9 text-cognac-500 mb-2" />
                <p className="text-sm text-slate-500 mb-3">Posez une question, ou choisissez :</p>
                <div className="grid gap-2 w-full">
                  {PROMPTS.map((p) => (
                    <button key={p} onClick={() => send(p)} data-testid="assistant-prompt"
                      className="text-left text-xs px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:border-cognac-400 hover:bg-cognac-50/50 transition-colors">{p}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-navy" : "bg-cognac-500"}`}>
                  {m.role === "user" ? <User className="w-3.5 h-3.5 text-white" /> : <Bot className="w-3.5 h-3.5 text-white" />}
                </div>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-navy text-white" : "bg-slate-100 text-navy"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-slate-100 flex gap-2 shrink-0">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Posez votre question…" data-testid="assistant-input"
              className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cognac-400" />
            <button onClick={() => send()} disabled={streaming} data-testid="assistant-send"
              className="px-4 rounded-xl bg-cognac-500 text-white font-semibold hover:bg-cognac-600 disabled:opacity-60 flex items-center"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
